import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Dialog,
  IconButton,
  Button,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const Gallery = () => {
  const { t, i18n } = useTranslation();
  const [selectedTag, setSelectedTag] = useState("all");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [visibleCount, setVisibleCount] = useState(20);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    const imagesData = t("gallery.images", { returnObjects: true });
    const allImages = [];

    Object.entries(imagesData).forEach(([category, images]) => {
      images.forEach((image, index) => {
        allImages.push({
          id: `${category}-${index}`,
          category,
          image,
        });
      });
    });

    setGalleryImages(allImages);
  }, [i18n.language, t]);

  const categories = ["all", ...new Set(galleryImages.map((img) => img.category))];

  const filteredImages =
    selectedTag === "all"
      ? galleryImages
      : galleryImages.filter((img) => img.category === selectedTag);

  const visibleImages = filteredImages.slice(0, visibleCount);

  const handleShowMore = () => setVisibleCount((prev) => prev + 20);

  const handleNext = () => {
    if (selectedIndex < filteredImages.length - 1)
      setSelectedIndex(selectedIndex + 1);
  };

  const handlePrev = () => {
    if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
  };

  return (
    <Box
      className="gallery-container"
      sx={{
        backgroundColor: "#FFF8E1",
        color: "#000",
        minHeight: "100vh",
        py: 8,
        px: { xs: 2, sm: 4, md: 8 },
        overflowX: "hidden",
      }}
    >
      {/* 🪔 Title */}
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 700,
          color: "#000",
          textShadow: "0 0 8px rgba(0, 0, 0, 0.3)",
        }}
      >
        {t("gallery.title")}
      </Typography>

      {/* 🏷️ Category Tabs */}
      <Stack
        direction="row"
        justifyContent="center"
        flexWrap="wrap"
        spacing={2}
        sx={{ mb: 6 }}
      >
        {categories.map((cat) => (
          <Button
            key={cat}
            onClick={() => {
              setSelectedTag(cat);
              setVisibleCount(20);
              setSelectedIndex(null);
            }}
            variant={selectedTag === cat ? "contained" : "outlined"}
            sx={{
              color: selectedTag === cat ? "#000" : "#FFD700",
              borderColor: "#FFD700",
              backgroundColor: selectedTag === cat ? "#FFD700" : "#000",
              "&:hover": {
                backgroundColor: "#ffea70",
                color: "#000",
              },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
            }}
          >
            {t(`gallery.${cat}`)}
          </Button>
        ))}
      </Stack>

      {/* 🖼️ Image Grid */}
      <Grid container spacing={3} justifyContent="center">
  <AnimatePresence>
    {visibleImages.map((img, index) => (
      <Grid item xs={12} sm={6} md={4} key={img.id}>
        <motion.div
          layout
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          whileHover={{ scale: 1.05 }}
          onClick={() => setSelectedIndex(index)}
          style={{
            cursor: "pointer",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#000",
            border: "2px solid #FFD700",
            boxShadow: "0 0 12px rgba(255, 215, 0, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "250px", // fixed height for uniform grid boxes
            width: "250px",
          }}
        >
          <img
            src={img.image}
            alt={`Gallery ${img.id}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </motion.div>
      </Grid>
    ))}
  </AnimatePresence>
</Grid>


      {/* 🔘 Show More / Show Less Button */}
{filteredImages.length > 20 && (
  <Box textAlign="center" mt={6}>
    <Button
      onClick={() =>
        visibleCount >= filteredImages.length
          ? setVisibleCount(20)
          : setVisibleCount((prev) => prev + 20)
      }
      sx={{
        backgroundColor: "#FFD700",
        color: "#000",
        px: 4,
        py: 1,
        borderRadius: "50px",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ffea70",
        },
      }}
    >
      {visibleCount >= filteredImages.length
        ? t("gallery.showLess")
        : t("gallery.showMore")}
    </Button>
  </Box>
)}


      {/* 🌟 Popup with Navigation */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <Dialog
            open={selectedIndex !== null}
            onClose={() => setSelectedIndex(null)}
            maxWidth="md"
            PaperProps={{
              sx: {
                background: "rgba(0, 0, 0, 0.95)",
                boxShadow: "0 0 20px rgba(255, 215, 0, 0.4)",
                borderRadius: "12px",
              },
            }}
          >
            <Box sx={{ position: "relative", textAlign: "center" }}>
              {/* Close */}
              <IconButton
                onClick={() => setSelectedIndex(null)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: "#FFD700",
                  "&:hover": { color: "#fff" },
                  zIndex: 2,
                }}
              >
                <CloseIcon fontSize="large" />
              </IconButton>

              {/* Prev */}
              {selectedIndex > 0 && (
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 10,
                    color: "#FFD700",
                    transform: "translateY(-50%)",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  <ArrowBackIosNewIcon fontSize="large" />
                </IconButton>
              )}

              {/* Next */}
              {selectedIndex < filteredImages.length - 1 && (
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 10,
                    color: "#FFD700",
                    transform: "translateY(-50%)",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  <ArrowForwardIosIcon fontSize="large" />
                </IconButton>
              )}

              {/* Image */}
              <motion.img
                key={filteredImages[selectedIndex].image}
                src={filteredImages[selectedIndex].image}
                alt="Selected"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.4 }}
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "12px",
                  display: "block",
                }}
              />
            </Box>
          </Dialog>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Gallery;
